import React, { useState, useEffect } from 'react';
import {
    Text,
    makeStyles,
    Spinner,
} from '@fluentui/react-components';
import { useTokenService } from '../../tokenService';
import { CopilotService } from '../../Service/ServerAPI';
import { useSelector } from 'react-redux';
import { RootState } from '../../utils/store';
import JsonViewer from './JsonViewer';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        height: '100%',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px',
        gap: '12px',
    },
    responseSection: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
});

const DebugLatestDates: React.FC<{ dataset?: string }> = ({ dataset }) => {
    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(true);
    const [response, setResponse] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);

    const getAccessToken = useTokenService();
    const copilotService = CopilotService.getInstance(getAccessToken);
    const settings = useSelector((state: RootState) => state.settings);

    useEffect(() => {
        const fetchLatestDates = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const result = await copilotService.getLatestDates(
                    settings.apiEndpoint,
                    dataset
                );

                if (result.error) {
                    setError(result.error);
                    setResponse(null);
                } else {
                    setResponse(result);
                    setError(null);
                }
            } catch (err) {
                const errorMessage =
                    err instanceof Error ? err.message : 'An unexpected error occurred';
                setError(errorMessage);
                setResponse(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLatestDates();
    }, [dataset]); // Re-fetch when dataset changes

    if (isLoading) {
        return (
            <div className={classes.container}>
                <div className={classes.loadingContainer}>
                    <Spinner size="medium" />
                    <Text>Loading latest dates...</Text>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <Text size={500} weight="semibold">Latest Dates from User Documents</Text>
                <Text size={300}>
                    This shows the most recent date values found in the user document database.
                </Text>
            </div>

            <div className={classes.responseSection}>
                <JsonViewer data={response} error={error} />
            </div>
        </div>
    );
};

export default DebugLatestDates;